import React, { useEffect, useState, useMemo } from "react";
import { collection, onSnapshot, query } from "firebase/firestore";
import { db } from "../firebase";
import useAuthUser from "../hooks/useAuthUser";
import { getPlayerPortfolio } from "../utils/portfolioUtils";
import { formatRarityName } from "../utils/formatRarityName";

export default function PlayerInventoryTab({ playerName, profile }) {
  const authUser = useAuthUser();
  const token = authUser?.token;
  const effectiveName = playerName || profile?.userDisplayName;

  const [databaseItems, setDatabaseItems] = useState([]);
  const [playerHoldings, setPlayerHoldings] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [showOnlyOwned, setShowOnlyOwned] = useState(true); 

  const [sortBy, setSortBy] = useState("default");
  const [isSortOpen, setIsSortOpen] = useState(false);

  const sortOptions = [
    { label: "DEFAULT", value: "default" },
    { label: "RARITY DESC", value: "rarity-down" },
    { label: "RARITY ASC", value: "rarity-up" },
    { label: "NAME A-Z", value: "name-asc" },
    { label: "QUANTITY", value: "qty" }
  ];

  const getRarityValue = (rarity) => {
    switch (rarity?.toLowerCase()) {
      case 'legendary': return 4;
      case 'epic': return 3;
      case 'rare': return 2;
      case 'uncommon': return 1;
      default: return 0;
    }
  };

  const getRarityColor = (rarity) => {
    switch (rarity?.toLowerCase()) {
      case 'legendary': return '#ffb100';
      case 'epic': return '#a335ee';
      case 'rare': return '#0070dd';
      case 'uncommon': return '#1eff00';
      default: return '#888888';
    }
  };

  useEffect(() => {
    const closeMenu = () => setIsSortOpen(false);
    window.addEventListener("click", closeMenu);
    return () => window.removeEventListener("click", closeMenu);
  }, []);

  useEffect(() => {
    const q = query(collection(db, "Item_Database"));
    const unsub = onSnapshot(q, (snap) => {
      setDatabaseItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!effectiveName || !token) return;
    async function fetchInventory() {
      try {
        setLoading(true);
        const data = await getPlayerPortfolio(effectiveName, token);
        setPlayerHoldings(data?.itemHoldings || []);
      } catch (err) {
        console.error("Inventory Fetch Error:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchInventory();
  }, [effectiveName, token]);

  const userItemsMap = useMemo(() => {
    const map = {};
    playerHoldings.forEach(holding => {
      map[holding.id] = holding;
    });
    return map;
  }, [playerHoldings]);

  const processedDisplayItems = useMemo(() => {
    let sourceList = showOnlyOwned 
      ? databaseItems.filter(item => !!userItemsMap[item.id]) 
      : databaseItems;

    let items = sourceList.map(dbItem => {
      const ownedInfo = userItemsMap[dbItem.id];
      return {
        ...dbItem,
        quantity: ownedInfo?.quantity || 0,
        isOwned: !!ownedInfo,
        name: dbItem?.name || dbItem.id.replace(/_/g, " ")
      };
    });

    if (searchTerm.trim()) {
      const queryStr = searchTerm.toLowerCase();
      items = items.filter(item => 
        item.name.toLowerCase().includes(queryStr) || 
        item.id.toLowerCase().includes(queryStr) ||
        item.description?.toLowerCase().includes(queryStr)
      );
    }

    return items.sort((a, b) => {
      if (sortBy === "default" || !sortBy) {
        const rarityDiff = getRarityValue(b.rarity) - getRarityValue(a.rarity);
        if (rarityDiff !== 0) return rarityDiff;
        return a.name.localeCompare(b.name);
      }
      if (sortBy === "rarity-down") return getRarityValue(b.rarity) - getRarityValue(a.rarity);
      if (sortBy === "rarity-up") return getRarityValue(a.rarity) - getRarityValue(b.rarity);
      if (sortBy === "name-asc") return a.name.localeCompare(b.name);
      if (sortBy === "qty") return (b.quantity || 0) - (a.quantity || 0);
      return 0;
    });
  }, [databaseItems, userItemsMap, showOnlyOwned, searchTerm, sortBy]);

  if (loading && databaseItems.length === 0) {
    return <p style={{ color: "#50E3C2", fontSize: "0.7rem", padding: "20px", fontFamily: "'JetBrains Mono', monospace" }} className="blink">LOADING...</p>;
  }

  return (
    <div style={{ fontFamily: "'JetBrains Mono', monospace", color: "#eee", background: "#050505", border: "1px solid rgba(80, 227, 194, 0.15)" }}>
      <div style={{ 
        padding: '12px', 
        background: 'rgba(80, 227, 194, 0.02)', 
        borderBottom: '1px solid #222',
        position: 'relative' 
      }}>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <input 
            type="text" 
            placeholder="SEARCH"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%', 
              background: '#0a0a0a', 
              border: '1px solid #333',
              borderLeft: `2px solid #50E3C2`,
              color: '#50E3C2', 
              padding: '10px 12px', 
              fontSize: '0.75rem', 
              outline: 'none',
              fontFamily: "'JetBrains Mono', monospace",
              transition: 'all 0.2s ease',
              boxShadow: 'inset 0 0 5px rgba(0,0,0,0.5)'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#50E3C2';
              e.target.style.boxShadow = '0 0 10px rgba(80, 227, 194, 0.2)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#333';
              e.target.style.boxShadow = 'inset 0 0 5px rgba(0,0,0,0.5)';
            }}
          />
        </div>
      </div>
      <div style={{ 
        padding: '0 0 0 12px', background: 'rgba(80, 227, 194, 0.04)', borderBottom: '1px solid #222', 
        display: 'flex', justifyContent: 'space-between', alignItems: 'stretch', fontSize: '0.6rem', height: '40px'
      }}>
        <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
          <div 
            onClick={() => setShowOnlyOwned(false)} 
            style={{ 
              cursor: 'pointer', 
              color: !showOnlyOwned ? '#50E3C2' : '#666', 
              borderBottom: !showOnlyOwned ? '2px solid #50E3C2' : '2px solid transparent',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              fontWeight: 'bold',
              transition: 'all 0.15s ease'
            }}
          >
           DATABASE ({databaseItems.length})
          </div>
          
          <div 
            onClick={() => setShowOnlyOwned(true)} 
            style={{ 
              cursor: 'pointer', 
              color: showOnlyOwned ? '#50E3C2' : '#666', 
              borderBottom: showOnlyOwned ? '2px solid #50E3C2' : '2px solid transparent',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              fontWeight: 'bold',
              transition: 'all 0.15s ease'
            }}
          >
            OWNED ({playerHoldings.length})
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'stretch' }}>
          <div 
            onClick={(e) => { e.stopPropagation(); setIsSortOpen(!isSortOpen); }}
            style={{ 
              display: "flex", 
              flexDirection: "column", 
              background: isSortOpen ? "rgba(80, 227, 194, 0.1)" : "rgba(0,0,0,0.2)", 
              padding: "0 16px", 
              borderLeft: "1px solid #222", 
              cursor: "pointer", 
              position: "relative",
              minWidth: "150px",
              justifyContent: "center"
            }}
          >
            <label style={{ color: "#50E3C2", opacity: 0.4, fontSize: "0.45rem", marginBottom: "1px", letterSpacing: "1px" }}>
              SORT
            </label>
            <div style={{ color: "#50E3C2", fontSize: "0.65rem", display: "flex", justifyContent: "space-between", alignItems: 'center', fontWeight: "bold" }}>
              {sortOptions.find(o => o.value === sortBy)?.label} 
              <span style={{ color: '#50E3C2', opacity: 0.6 }}>{isSortOpen ? "▴" : "▾"}</span>
            </div>

            {isSortOpen && (
              <div style={{ 
                position: "absolute", 
                top: "100%", 
                right: -1,
                width: "100.5%", 
                zIndex: 110, 
                background: "#050505", 
                border: "1px solid #50E3C2", 
                borderTop: "none",
                boxShadow: "0 10px 30px rgba(0,0,0,0.9)"
              }}>
                {sortOptions.map(opt => (
                  <div 
                    key={opt.value} 
                    onClick={() => { setSortBy(opt.value); setIsSortOpen(false); }}
                    style={{ 
                      padding: "10px 12px", 
                      color: sortBy === opt.value ? "#000" : "#50E3C2", 
                      background: sortBy === opt.value ? "#50E3C2" : "transparent",
                      fontSize: "0.6rem", 
                      borderBottom: "1px solid rgba(80, 227, 194, 0.05)",
                      transition: "all 0.1s ease"
                    }}
                    onMouseEnter={(e) => {
                      if (sortBy !== opt.value) e.target.style.background = "rgba(80, 227, 194, 0.1)";
                    }}
                    onMouseLeave={(e) => {
                      if (sortBy !== opt.value) e.target.style.background = "transparent";
                    }}
                  >
                    {opt.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="scrollable-content" style={{ maxHeight: '600px', padding: '16px', overflowY: 'auto' }}>
        {processedDisplayItems.length === 0 ? (
          <p style={{ padding: "30px", color: "#555", fontSize: "0.65rem", textAlign: 'center', border: '1px dashed #222', letterSpacing: '1px' }}>
            NO ASSET FOUND
          </p>
        ) : (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(145px, 1fr))', 
            gap: '14px' 
          }}>
            {processedDisplayItems.map((item) => {
              const rarityColor = getRarityColor(item.rarity);
              
              return (
                <div key={item.id} style={{
                  border: `1px solid ${item.isOwned ? rarityColor : '#222'}`, 
                  padding: '12px', 
                  textAlign: 'center',
                  background: item.isOwned ? `linear-gradient(180deg, ${rarityColor}08 0%, rgba(0,0,0,0.45) 100%)` : 'transparent', 
                  opacity: item.isOwned ? 1 : 0.22,
                  position: 'relative', 
                  display: 'flex', 
                  flexDirection: 'column', 
                  justifyContent: 'space-between'
                }}>
                  {item.isOwned && (
                    <div style={{ 
                      position: 'absolute', top: 0, right: 0, 
                      background: rarityColor, color: '#000', 
                      fontSize: '0.5rem', padding: '2px 5px', fontWeight: 'bold' 
                    }}>
                      x{item.quantity}
                    </div>
                  )}
                  <div style={{ width: '100%', height: '60px', display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '10px' }}>
                    <img 
                      src={`/items/${item.id}.png`} 
                      alt={item.name}
                      style={{ 
                        maxWidth: '90%', 
                        maxHeight: '90%', 
                        objectFit: 'contain', 
                        filter: item.isOwned ? `drop-shadow(0 0 4px ${rarityColor}88)` : 'none' 
                      }}
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.parentElement.innerHTML = `<span style="font-size: 1.4rem; color: ${rarityColor}; opacity: 0.6;">${item.rarity?.toLowerCase() === 'legendary' ? '✦' : '⬡'}</span>`;
                      }}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: '0.65rem', fontWeight: 'bold', color: '#fff', marginBottom: '2px', letterSpacing: '0.2px' }}>
                      {item.name}
                    </div>
                    <div style={{ fontSize: '0.48rem', color: rarityColor, margin: '2px 0 6px 0', textTransform: 'uppercase', fontWeight: 'bold', letterSpacing: '0.5px' }}>
                      {formatRarityName(item.rarity || 'COMMON')}
                    </div>
                  </div>

<div style={{ 
  fontSize: '0.52rem', 
  color: '#fff', 
  fontStyle: 'italic',
  marginTop: '6px', 
  paddingTop: '8px', 
  borderTop: '1px solid rgba(255,255,255,0.04)', 
  lineHeight: '1.3', 
  minHeight: '3.6em', 
  display: 'flex', 
  alignItems: 'center', 
  justifyContent: 'center' 
}}>
  {item.description || "No data."}
</div>

                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}